import { useState, useEffect, useCallback } from 'react';
import {
  fetchWorkCatalog, createWorkCatalogItem,
  updateWorkCatalogItem, deleteWorkCatalogItem, renameWorkCategory, reorderWorkCategory,
  exportWorkCatalog, importWorkCatalogPreview, importWorkCatalogConfirm,
} from '@/lib/api';
import { formatMXN } from '@/lib/formatters';
import { setGlobalCosts, fetchCostsSummary, fetchWorkCatalogs, createWorkCatalog_v2, updateWorkCatalog_v2, cloneWorkCatalog } from '@/lib/api';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, ClipboardList, AlertTriangle, Check,
  ChevronDown, ChevronRight, GripVertical, Download, Upload,
} from 'lucide-react';

/* ── Helpers ── */
function buildCategories(items) {
  const map = {};
  for (const item of items) {
    const c = item.category_name;
    if (!map[c]) map[c] = { name: c, category_order: item.category_order || 0, items: [], total_weight: 0 };
    map[c].items.push(item);
    if (item.is_active !== false) map[c].total_weight += (item.weight_percent || 0);
  }
  const list = Object.values(map);
  list.sort((a, b) => a.category_order - b.category_order || a.name.localeCompare(b.name));
  // Sort items within each category
  for (const cat of list) {
    cat.items.sort((a, b) => (a.item_order || 0) - (b.item_order || 0));
  }
  return list;
}

/* ════════════════════════════════════════════
   MAIN: Catálogo de Obra
   ════════════════════════════════════════════ */
export default function CatalogoObra() {
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [renamingCat, setRenamingCat] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [activeCatalog, setActiveCatalog] = useState('default');
  const [newCatalogOpen, setNewCatalogOpen] = useState(false);

  const loadCatalogs = useCallback(async () => {
    try { setCatalogs(await fetchWorkCatalogs()); } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      const items = await fetchWorkCatalog(null, false, activeCatalog);
      setAllItems(items);
      setCategories(buildCategories(items));
    } catch {
      toast.error('Error al cargar catálogo');
    } finally {
      setLoading(false);
    }
  }, [activeCatalog]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);
  useEffect(() => { load(); }, [load]);

  const activeItems = allItems.filter(i => i.is_active !== false);
  const totalWeight = activeItems.reduce((s, i) => s + (i.weight_percent || 0), 0);
  const isBalanced = Math.abs(totalWeight - 100) < 0.1;

  /* ── Actions ── */
  const toggleCat = (name) => setCollapsedCats(c => ({ ...c, [name]: !c[name] }));

  const handleToggleActive = async (item) => {
    try {
      await updateWorkCatalogItem(item.id, { is_active: !item.is_active });
      toast.success(item.is_active ? 'Concepto desactivado' : 'Concepto activado');
      load();
    } catch { toast.error('Error al actualizar'); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWorkCatalogItem(deleteTarget.id);
      toast.success(`"${deleteTarget.concept_name}" eliminado`);
      load();
    } catch { toast.error('Error al eliminar'); }
    finally { setDeleteTarget(null); }
  };

  const handleSaved = () => { setDlgOpen(false); setEditTarget(null); load(); };

  const handleInlineWeight = async (itemId, value) => {
    try {
      await updateWorkCatalogItem(itemId, { weight_percent: value });
      load();
    } catch { toast.error('Error al guardar peso'); }
  };

  const handleInlineOrder = async (itemId, value) => {
    try {
      await updateWorkCatalogItem(itemId, { item_order: value });
      load();
    } catch { toast.error('Error al guardar orden'); }
  };

  const handleRenameCategory = async (oldName) => {
    if (!renameValue.trim() || renameValue.trim() === oldName) { setRenamingCat(null); return; }
    try {
      await renameWorkCategory(oldName, renameValue.trim());
      toast.success(`Partida renombrada: ${oldName} → ${renameValue.trim()}`);
      setRenamingCat(null);
      load();
    } catch { toast.error('Error al renombrar partida'); }
  };

  const handleReorderCategory = async (catName, newOrder) => {
    try {
      await reorderWorkCategory(catName, newOrder);
      load();
    } catch { toast.error('Error al reordenar'); }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="space-y-6" data-testid="catalogo-obra-loading">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-[500px] rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="catalogo-obra-page">
      <PageHeader title="Catálogo de Obra" subtitle={`${activeItems.length} conceptos activos en ${categories.length} partidas`}>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => exportWorkCatalog().catch(() => toast.error('Error al exportar'))} data-testid="catalog-export-button">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Exportar CSV
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => setImportOpen(true)} data-testid="catalog-import-button">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Importar CSV
          </Button>
          <Button onClick={() => { setEditTarget(null); setDlgOpen(true); }} data-testid="catalog-add-button">
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo Concepto
          </Button>
        </div>
      </PageHeader>

      {/* Catalog selector */}
      {catalogs.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {catalogs.map(c => (
              <Button key={c.id} size="sm" variant={activeCatalog === c.id ? 'default' : 'outline'} className="text-[11px]" onClick={() => setActiveCatalog(c.id)}>
                {c.name} ({c.item_count || 0})
              </Button>
            ))}
          </div>
          <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => {
            const name = prompt('Nombre del nuevo catálogo:');
            if (name) createWorkCatalog_v2({ name }).then(() => { loadCatalogs(); toast.success('Catálogo creado'); }).catch(() => toast.error('Error'));
          }}><Plus className="h-3 w-3 mr-1" /> Nuevo Catálogo</Button>
          {activeCatalog && (
            <>
              <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => {
                const name = prompt('Nombre del catálogo clonado:');
                if (name) cloneWorkCatalog(activeCatalog, { name }).then(() => { loadCatalogs(); toast.success('Catálogo clonado'); }).catch(() => toast.error('Error'));
              }}>Clonar</Button>
              <Button size="sm" variant="ghost" className="text-xs text-slate-500" onClick={() => {
                const current = catalogs.find(c => c.id === activeCatalog);
                const name = prompt('Renombrar catálogo:', current?.name || '');
                if (name && name !== current?.name) updateWorkCatalog_v2(activeCatalog, { name }).then(() => { loadCatalogs(); toast.success('Renombrado'); }).catch(() => toast.error('Error'));
              }}>Renombrar</Button>
            </>
          )}
        </div>
      )}

      {/* ── Weight validation card ── */}
      <WeightSummaryCard
        totalWeight={totalWeight}
        isBalanced={isBalanced}
        categories={categories}
      />

      {/* ── Global Costs Panel ── */}
      <GlobalCostsPanel onApplied={load} />

      {/* ── Catalog table ── */}
      {allItems.length === 0 ? (
        <EmptyState
          title="Catálogo vacío"
          description="Agrega las partidas y conceptos de obra para comenzar a dar seguimiento al avance."
          icon={ClipboardList}
        />
      ) : (
        <Card className="rounded-xl border bg-white overflow-hidden" data-testid="catalogo-card">
          <Table data-testid="catalogo-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 w-10" />
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Partida / Concepto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center w-16">Orden</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right w-20">Peso %</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right w-24">Materiales</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right w-24">Mano Obra</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center w-20">Activo</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map(cat => {
                const isCollapsed = collapsedCats[cat.name];
                const activeCount = cat.items.filter(i => i.is_active !== false).length;
                return [
                  /* ── Category header row ── */
                  <TableRow key={`cat-${cat.name}`} className="bg-slate-50/80 hover:bg-slate-100/60 border-t border-slate-200/60">
                    <TableCell className="py-3 px-3">
                      <button
                        onClick={() => toggleCat(cat.name)}
                        className="h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
                        data-testid={`cat-toggle-${cat.name}`}
                      >
                        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="py-3 px-3">
                      {renamingCat === cat.name ? (
                        <Input
                          autoFocus
                          className="h-7 text-xs font-bold uppercase w-56"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => handleRenameCategory(cat.name)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameCategory(cat.name);
                            if (e.key === 'Escape') setRenamingCat(null);
                          }}
                          data-testid={`cat-rename-input-${cat.name}`}
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[11px] font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:text-[#F05A28] transition-colors"
                            onClick={() => { setRenamingCat(cat.name); setRenameValue(cat.name); }}
                            title="Clic para renombrar partida"
                            data-testid={`cat-name-${cat.name}`}
                          >
                            {cat.name}
                          </span>
                          <Pencil className="h-2.5 w-2.5 text-slate-300" />
                          <Badge variant="outline" className="text-[9px] font-normal text-slate-400 border-slate-200 py-0">
                            {activeCount} concepto{activeCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-3 px-3 text-center">
                      <InlineEdit
                        value={cat.category_order || 0}
                        onSave={v => handleReorderCategory(cat.name, v)}
                        type="number"
                        className="font-semibold text-slate-600"
                        data-testid={`cat-order-${cat.name}`}
                      />
                    </TableCell>
                    <TableCell className="py-3 px-3 text-right">
                      <span className="text-xs font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {cat.total_weight.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>,

                  /* ── Concept rows ── */
                  ...(!isCollapsed ? cat.items.map(item => (
                    <TableRow
                      key={item.id}
                      className={`table-row-hover group ${item.is_active === false ? 'opacity-35' : ''}`}
                      data-testid={`concept-row-${item.id}`}
                    >
                      <TableCell className="py-2.5 px-3">
                        <GripVertical className="h-3 w-3 text-slate-200 mx-auto" />
                      </TableCell>
                      <TableCell className="py-2.5 px-3 pl-10">
                        <span className="text-[13px] text-slate-700">{item.concept_name}</span>
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-center">
                        <InlineEdit
                          value={item.item_order}
                          onSave={v => handleInlineOrder(item.id, v)}
                          type="number"
                          data-testid={`concept-order-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right">
                        <InlineEdit
                          value={item.weight_percent}
                          onSave={v => handleInlineWeight(item.id, v)}
                          type="number"
                          suffix="%"
                          data-testid={`concept-weight-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums text-slate-500">{item.cost_materials ? formatMXN(item.cost_materials) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums text-slate-500">{item.cost_labor ? formatMXN(item.cost_labor) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-3 text-center">
                        <Switch
                          checked={item.is_active !== false}
                          onCheckedChange={() => handleToggleActive(item)}
                          className="scale-75"
                          data-testid={`concept-active-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost" size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-slate-700"
                                onClick={() => { setEditTarget(item); setDlgOpen(true); }}
                                data-testid={`concept-edit-${item.id}`}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top"><p className="text-xs">Editar</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost" size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-red-500"
                                onClick={() => setDeleteTarget(item)}
                                data-testid={`concept-delete-${item.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top"><p className="text-xs">Eliminar</p></TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : []),
                ];
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ── Create / Edit Dialog ── */}
      <ConceptDialog
        open={dlgOpen}
        onClose={() => { setDlgOpen(false); setEditTarget(null); }}
        onSaved={handleSaved}
        initial={editTarget}
        existingCategories={categories.map(c => c.name)}
      />

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar concepto</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>"{deleteTarget?.concept_name}"</strong> de la partida <strong>{deleteTarget?.category_name}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Import Dialog ── */}
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
    </div>
  );
}


/* ════════════════════════════════════════════
   WEIGHT SUMMARY CARD
   ════════════════════════════════════════════ */
function WeightSummaryCard({ totalWeight, isBalanced, categories }) {
  const diff = totalWeight - 100;
  return (
    <Card className="rounded-xl border bg-white" data-testid="weight-summary-card">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Peso Total del Catálogo</p>
          <div className="flex items-center gap-2">
            <span
              className={`text-xl font-bold tabular-nums ${isBalanced ? 'text-emerald-700' : 'text-amber-600'}`}
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="total-weight-value"
            >
              {totalWeight.toFixed(1)}%
            </span>
            {isBalanced ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="weight-balanced-badge">
                <Check className="h-3 w-3 mr-0.5" /> Balanceado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid="weight-unbalanced-badge">
                <AlertTriangle className="h-3 w-3 mr-0.5" />
                {diff < 0 ? `Faltan ${Math.abs(diff).toFixed(1)}%` : `Excede ${diff.toFixed(1)}%`}
              </Badge>
            )}
          </div>
        </div>

        {/* Main progress bar */}
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden mb-4">
          <div
            className={`h-full rounded-full transition-all duration-500 ${isBalanced ? 'bg-emerald-500' : diff > 0 ? 'bg-amber-400' : 'bg-amber-400'}`}
            style={{ width: `${Math.min(totalWeight, 100)}%` }}
          />
        </div>

        {/* Per-category breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-5 gap-y-2.5">
          {categories.map(cat => {
            const pct = totalWeight > 0 ? (cat.total_weight / totalWeight * 100) : 0;
            return (
              <div key={cat.name} className="flex items-center gap-2" data-testid={`weight-cat-${cat.name}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] text-slate-500 truncate pr-1">{cat.name}</span>
                    <span className="text-[10px] tabular-nums font-semibold text-slate-700 shrink-0">{cat.total_weight.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#F05A28] transition-all duration-300"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


/* ════════════════════════════════════════════
   CREATE / EDIT DIALOG
   ════════════════════════════════════════════ */
function ConceptDialog({ open, onClose, onSaved, initial, existingCategories }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    category_name: '',
    concept_name: '',
    item_order: '',
    weight_percent: '',
    unit: '',
    is_active: true,
  });
  const [newCatMode, setNewCatMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reset form when dialog opens / initial changes
  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          category_name: initial.category_name || '',
          concept_name: initial.concept_name || '',
          item_order: initial.item_order ?? '',
          weight_percent: initial.weight_percent ?? '',
          unit: initial.unit || '',
          is_active: initial.is_active !== false,
          cost_materials: initial.cost_materials ?? '',
          cost_labor: initial.cost_labor ?? '',
        });
        setNewCatMode(false);
      } else {
        setForm({ category_name: '', concept_name: '', item_order: '', weight_percent: '', unit: '', is_active: true, cost_materials: '', cost_labor: '' });
        setNewCatMode(existingCategories.length === 0);
      }
    }
  }, [open, initial, existingCategories.length]);

  const handleSave = async () => {
    if (!form.category_name.trim() || !form.concept_name.trim()) return;
    setSaving(true);
    try {
      const data = {
        category_name: form.category_name.trim(),
        concept_name: form.concept_name.trim(),
        item_order: parseInt(form.item_order) || 0,
        weight_percent: parseFloat(form.weight_percent) || 0,
        unit: form.unit.trim() || null,
        is_active: form.is_active,
        cost_materials: parseFloat(form.cost_materials) || null,
        cost_labor: parseFloat(form.cost_labor) || null,
      };
      if (isEdit) {
        await updateWorkCatalogItem(initial.id, data);
        toast.success('Concepto actualizado');
      } else {
        await createWorkCatalogItem(data);
        toast.success(`Concepto "${data.concept_name}" creado`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="concept-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#F05A28]" />
            {isEdit ? 'Editar Concepto' : 'Nuevo Concepto'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ── Category selector ── */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Partida</Label>
            {!newCatMode && existingCategories.length > 0 ? (
              <div className="flex gap-2">
                <Select value={form.category_name} onValueChange={v => set('category_name', v)}>
                  <SelectTrigger className="flex-1" data-testid="category-select">
                    <SelectValue placeholder="Seleccionar partida" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingCategories.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline" size="sm"
                  className="h-9 shrink-0 text-xs"
                  onClick={() => { setNewCatMode(true); set('category_name', ''); }}
                  data-testid="new-category-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Nueva
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  autoFocus={!isEdit}
                  value={form.category_name}
                  onChange={e => set('category_name', e.target.value)}
                  placeholder="Nombre de la nueva partida"
                  className="flex-1"
                  data-testid="category-name-input"
                />
                {existingCategories.length > 0 && (
                  <Button
                    variant="outline" size="sm"
                    className="h-9 shrink-0 text-xs"
                    onClick={() => setNewCatMode(false)}
                  >
                    Existente
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ── Concept name ── */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Concepto</Label>
            <Input
              value={form.concept_name}
              onChange={e => set('concept_name', e.target.value)}
              placeholder="Nombre del concepto"
              data-testid="concept-name-input"
            />
          </div>

          {/* ── Order + Weight + Unit ── */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Orden</Label>
              <Input
                type="number" min="0"
                value={form.item_order}
                onChange={e => set('item_order', e.target.value)}
                placeholder="1"
                data-testid="concept-order-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Peso %</Label>
              <Input
                type="number" step="0.1" min="0" max="100"
                value={form.weight_percent}
                onChange={e => set('weight_percent', e.target.value)}
                placeholder="5.0"
                data-testid="concept-weight-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Unidad</Label>
              <Input
                value={form.unit}
                onChange={e => set('unit', e.target.value)}
                placeholder="m², ml, pza"
                data-testid="concept-unit-input"
              />
            </div>
          </div>

          {/* Costs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Costo Materiales</Label>
              <Input type="number" step="0.01" value={form.cost_materials} onChange={e => set('cost_materials', e.target.value)} placeholder="$0.00" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Costo Mano de Obra</Label>
              <Input type="number" step="0.01" value={form.cost_labor} onChange={e => set('cost_labor', e.target.value)} placeholder="$0.00" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="concept-dialog-cancel">Cancelar</Button>
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={!form.category_name.trim() || !form.concept_name.trim() || saving}
            data-testid="concept-dialog-save"
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Concepto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ════════════════════════════════════════════
   IMPORT DIALOG
   ════════════════════════════════════════════ */
function ImportDialog({ open, onClose, onDone }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useState(null);

  const reset = () => { setStep('upload'); setPreview(null); setItems([]); setResult(null); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await importWorkCatalogPreview(file);
      setPreview(data);
      setItems(data.items.map(it => ({ ...it, action: it.is_duplicate ? 'skip' : 'create' })));
      setStep('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al leer el archivo');
    } finally { setBusy(false); }
  };

  const setAction = (idx, action) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, action } : it));
  };

  const setAllDuplicateAction = (action) => {
    setItems(prev => prev.map(it => it.is_duplicate ? { ...it, action } : it));
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await importWorkCatalogConfirm(items);
      setResult(res);
      setStep('done');
      toast.success(res.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al importar');
    } finally { setBusy(false); }
  };

  const handleClose = () => { reset(); onClose(); };
  const handleFinish = () => { reset(); onDone(); };

  const newCount = items.filter(i => i.action === 'create').length;
  const overwriteCount = items.filter(i => i.action === 'overwrite').length;
  const skipCount = items.filter(i => i.action === 'skip').length;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#F05A28]" />
            {step === 'upload' ? 'Importar Catálogo' : step === 'preview' ? 'Vista Previa de Importación' : 'Importación Completada'}
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP 1: Upload ── */}
        {step === 'upload' && (
          <div className="py-6 space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-1">Selecciona un archivo CSV</p>
              <p className="text-[10px] text-slate-400 mb-4">Formato: Partida, Concepto, Orden, Peso %, Unidad, Activo</p>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="file" accept=".csv" className="hidden" onChange={handleFile} disabled={busy} data-testid="import-file-input" />
                <Button variant="outline" disabled={busy} asChild><span>{busy ? 'Leyendo...' : 'Seleccionar archivo'}</span></Button>
              </label>
            </div>
            <p className="text-[10px] text-slate-400">Tip: Exporta el catálogo actual como plantilla y edítalo en Excel antes de re-importar.</p>
          </div>
        )}

        {/* ── STEP 2: Preview ── */}
        {step === 'preview' && preview && (
          <div className="space-y-4 py-2">
            {/* Summary */}
            <div className="flex gap-3">
              <div className="flex-1 bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-700">{preview.new}</p>
                <p className="text-[9px] uppercase text-emerald-600">Nuevos</p>
              </div>
              <div className="flex-1 bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-amber-700">{preview.duplicates}</p>
                <p className="text-[9px] uppercase text-amber-600">Duplicados</p>
              </div>
              <div className="flex-1 bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-slate-700">{preview.total}</p>
                <p className="text-[9px] uppercase text-slate-500">Total</p>
              </div>
            </div>

            {/* Bulk action for duplicates */}
            {preview.duplicates > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-xs text-amber-700 flex-1">{preview.duplicates} ya existen en el catálogo.</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setAllDuplicateAction('overwrite')}>Sobrescribir todos</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setAllDuplicateAction('skip')}>Omitir todos</Button>
                </div>
              </div>
            )}

            {/* Items table */}
            <div className="max-h-[300px] overflow-auto rounded-lg border">
              <Table data-testid="import-preview-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-2.5">Partida</TableHead>
                    <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-2.5">Concepto</TableHead>
                    <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-2.5 text-right w-14">Peso %</TableHead>
                    <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-2.5 w-28">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={idx} className={it.is_duplicate ? 'bg-amber-50/40' : ''}>
                      <TableCell className="py-1.5 px-2.5 text-[11px] text-slate-600">{it.category_name}</TableCell>
                      <TableCell className="py-1.5 px-2.5 text-[11px] font-medium">{it.concept_name}</TableCell>
                      <TableCell className="py-1.5 px-2.5 text-[11px] tabular-nums text-right">{it.weight_percent}%</TableCell>
                      <TableCell className="py-1.5 px-2.5">
                        {it.is_duplicate ? (
                          <Select value={it.action} onValueChange={v => setAction(idx, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-full"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip">Omitir</SelectItem>
                              <SelectItem value="overwrite">Sobrescribir</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">Crear</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Action summary */}
            <div className="text-xs text-slate-500 flex gap-4">
              <span>Crear: <strong className="text-emerald-700">{newCount}</strong></span>
              <span>Sobrescribir: <strong className="text-amber-700">{overwriteCount}</strong></span>
              <span>Omitir: <strong className="text-slate-500">{skipCount}</strong></span>
            </div>
          </div>
        )}

        {/* ── STEP 3: Done ── */}
        {step === 'done' && result && (
          <div className="py-8 text-center space-y-3">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <Check className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-emerald-700">{result.message}</p>
            <div className="flex justify-center gap-4 text-xs text-slate-500">
              {result.created > 0 && <span>Creados: <strong>{result.created}</strong></span>}
              {result.overwritten > 0 && <span>Sobrescritos: <strong>{result.overwritten}</strong></span>}
              {result.skipped > 0 && <span>Omitidos: <strong>{result.skipped}</strong></span>}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && <Button variant="outline" onClick={handleClose}>Cancelar</Button>}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { reset(); }}>Cancelar</Button>
              <Button onClick={handleConfirm} disabled={busy || (newCount + overwriteCount === 0)} data-testid="import-confirm-btn">
                {busy ? 'Importando...' : `Importar ${newCount + overwriteCount} registro${newCount + overwriteCount !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
          {step === 'done' && <Button onClick={handleFinish}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ════════════════════════════════════════════
   INLINE EDITABLE CELL
   ════════════════════════════════════════════ */
function InlineEdit({ value, onSave, type = 'number', suffix = '', className = '', ...props }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);

  const commit = () => {
    setEditing(false);
    const parsed = type === 'number' ? (parseFloat(local) || 0) : local;
    if (parsed !== value) onSave(parsed);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        min="0"
        step={suffix === '%' ? '0.1' : '1'}
        className="h-6 w-16 text-center text-[11px] tabular-nums border border-slate-300 rounded px-1 outline-none focus:ring-1 focus:ring-[#F05A28] bg-white"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setLocal(value); setEditing(false); }
        }}
      />
    );
  }

  return (
    <span
      onClick={() => { setLocal(value); setEditing(true); }}
      className={`editable-cell cursor-pointer text-xs tabular-nums text-slate-500 ${className}`}
      {...props}
    >
      {value}{suffix}
    </span>
  );
}



/* ── Global Costs Panel ── */
function GlobalCostsPanel({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ total_materials: '', total_labor: '' });
  const [saving, setSaving] = useState(false);

  const loadSummary = async () => {
    try { setSummary(await fetchCostsSummary()); }
    catch {}
  };

  useEffect(() => { loadSummary(); }, []);

  const handleApply = async () => {
    const tm = parseFloat(form.total_materials) || 0;
    const tl = parseFloat(form.total_labor) || 0;
    if (!tm && !tl) return;
    setSaving(true);
    try {
      const res = await setGlobalCosts({ total_materials: tm, total_labor: tl });
      toast.success(res.message);
      setOpen(false);
      setForm({ total_materials: '', total_labor: '' });
      loadSummary();
      onApplied();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  if (!summary) return null;

  return (
    <Card className="rounded-xl border bg-white">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-[9px] uppercase tracking-widest text-slate-400">Total Materiales</p>
              <p className="text-[15px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(summary.total_materials)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-slate-400">Total Mano de Obra</p>
              <p className="text-[15px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(summary.total_labor)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-[#F05A28]">Costo Total Obra</p>
              <p className="text-[15px] font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(summary.total_cost)}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Distribuir Costo Global</Button>
        </div>

        {open && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <p className="text-xs text-slate-500">Ingresa el costo total de la obra. Se distribuirá proporcionalmente entre los conceptos según su peso %.</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Total Materiales</Label>
                <Input type="number" step="0.01" value={form.total_materials} onChange={e => setForm(f => ({ ...f, total_materials: e.target.value }))} placeholder="$0.00" className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Total Mano de Obra</Label>
                <Input type="number" step="0.01" value={form.total_labor} onChange={e => setForm(f => ({ ...f, total_labor: e.target.value }))} placeholder="$0.00" className="mt-1 h-9" />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={handleApply} disabled={saving || (!form.total_materials && !form.total_labor)}>{saving ? 'Aplicando...' : 'Aplicar'}</Button>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
